import React, { useState, useRef, useEffect } from "react";
import { faChevronDown, faBars, faClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import '../../css/FaqBody.css';
import Markdown from '../../utils/markdown';

const request = process.env.REACT_APP_PUBLIC_URL + '/request.svg';
const arrowLeft = process.env.REACT_APP_PUBLIC_URL + '/arrow-left.svg';
const lawInfo = process.env.REACT_APP_PUBLIC_URL + '/low-info.svg';

// API Configuration - Using backend proxy
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/chatbot';
const API_KEY = process.env.REACT_APP_CHATBOT_FUNCTION_KEY;

// FAQ Analytics logging function
const logFAQAnalytics = async (eventType, eventData) => {
  try {
    const payload = {
      eventType, // 'category_click' or 'question_click'
      sessionId: sessionStorage.getItem('sessionID'),
      employeeId: localStorage.getItem('EmployeeID'),
      language: localStorage.getItem('language') || 'en',
      channel: isMobile ? 'Mobile' : 'Portal',
      timestamp: new Date().toISOString(),
      ...eventData
    };

    await fetch(`${API_BASE_URL}/faq/analytics?code=${API_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  } catch (error) {
    // Silently fail - analytics should not block user experience
    console.error('FAQ analytics error:', error);
  }
};

// ---------- CollapseCard ----------
function CollapseCard({ title, children, isOpen, onClick }) {
  const contentRef = useRef(null);
  const [height, setHeight] = useState("0px");

  useEffect(() => {
    setHeight(isOpen ? `${contentRef.current.scrollHeight}px` : "0px");
  }, [isOpen]);

  return (
    <div className="card-faq">
      <button onClick={onClick} className="card-faq-header">
        <span>{title}</span>
        <span className="arrow-icon-faq">
          <FontAwesomeIcon
            icon={faChevronDown}
            style={{
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s ease",
            }}
          />
        </span>
      </button>

      <div
        className="card-body-faq"
        ref={contentRef}
        style={{
          height,
          overflow: "hidden",
          transition: "height 0.4s ease",
        }}
      >
        <div>{children}</div>
      </div>
    </div>
  );
}

// ---------- FAQs ----------
const FAQs = ({ setSelectedOption }) => {
  const { t, i18n } = useTranslation();
  const [activeCategoryNav, setActiveCategoryNav] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ---------- Accordion ----------
  function Accordion({ faqs }) {
    const [activeIndex, setActiveIndex] = useState(null);

    const toggleCard = (index, faq) => {
      const isOpening = activeIndex !== index;
      setActiveIndex((prev) => (prev === index ? null : index));

      // Log question click only when opening
      if (isOpening) {
        logFAQAnalytics('question_click', {
          categoryId: selectedCategory?.id,
          categoryName: selectedCategory?.category,
          questionId: faq.id,
          question: faq.question
        });
      }
    };

    if (!faqs || faqs.length === 0) {
      return (
        <div className="no-faqs-message">
          <p>{t("No FAQs available for this category.")}</p>
        </div>
      );
    }

    return (
      <div className="accordion-container">
        {faqs.map((faq, index) => (
          <CollapseCard
            key={faq.id}
            title={faq.question}
            isOpen={activeIndex === index}
            onClick={() => toggleCard(index, faq)}
          >
            {faq.answer ? (
              <Markdown
                content={faq.answer}
              />
            ) : (
              t("No answer provided yet.")
            )}
          </CollapseCard>
        ))}
      </div>
    );
  }

  // Get current language
  const currentLanguage = localStorage.getItem('language') === 'ar' ? 'ar' : 'en';

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, [currentLanguage]);

  // Fetch categories from API
  const fetchCategories = async () => {
    setLoading(true);
    setError(null);

    try {
      const url = `${API_BASE_URL}/faq/categories?code=${API_KEY}&language=${currentLanguage}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch categories: ${response.status}`);
      }

      const data = await response.json();
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(err.message);
      // Fallback to empty array
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch FAQs for a specific category
  const fetchFAQs = async (categoryId) => {
    setLoading(true);
    setError(null);

    try {
      const url = `${API_BASE_URL}/faq/questions?code=${API_KEY}&category_id=${categoryId}&language=${currentLanguage}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch FAQs: ${response.status}`);
      }

      const data = await response.json();
      setFaqs(data.faqs || []);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError(err.message);
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle category selection
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setActiveCategoryNav(false);
    fetchFAQs(category.id);

    // Log category click
    logFAQAnalytics('category_click', {
      categoryId: category.id,
      categoryName: category.category
    });
  };

  // Handle back button
  const handleBack = () => {
    if (selectedCategory) {
      setSelectedCategory(null);
      setFaqs([]);
    } else {
      setSelectedOption("");
    }
  };

  // Get icon for category (you can enhance this logic based on category names)
  const getCategoryIcon = (categoryName) => {
    // Add logic to map category names to icons
    if (categoryName.includes('إجازة') || categoryName.toLowerCase().includes('leave')) {
      return request;
    }
    return lawInfo;
  };

  return (
    <div className={`chatbot-body faq ${activeCategoryNav ? 'active' : ''}`}>
      <div
        className={`overlay-faq ${activeCategoryNav ? "active" : ""}`}
        onClick={() => setActiveCategoryNav(false)}
      ></div>

      {/* Sidebar Category Navigation */}
      <div className={`category-nav ${activeCategoryNav ? "active" : ""}`}>
        <div className="close" onClick={() => setActiveCategoryNav(false)}>
          <FontAwesomeIcon icon={faClose} />
        </div>
        <ul className="list-cat">
          {categories.map((cat) => (
            <li
              key={cat.id}
              className="list-cat-item"
              onClick={() => handleCategoryClick(cat)}
            >
              <div>{cat.category}</div>
              <img src={arrowLeft} alt="" />
            </li>
          ))}
        </ul>
      </div>

      {/* Header */}
      <div className='faq-header'>
        <ul className='faq-nav'>
          <li className='faq-nav-item back' onClick={handleBack}>
            <img src={arrowLeft} alt="" />
            <div>{t('back')}</div>
          </li>
          <li className='faq-nav-item divider'></li>
          <li className='faq-nav-item active'>
            {selectedCategory ? selectedCategory.category : t('HR Law Information')}
          </li>
          <li className="faq-nav-item bar-icon" onClick={() => setActiveCategoryNav(true)}>
            <FontAwesomeIcon icon={faBars} />
          </li>
        </ul>
      </div>

      {/* Body */}
      <div className='faq-body'>
        {/* Loading State */}
        {loading && (
          <div className="loading-message">
            <p>{t('Loading...')}</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="error-message">
            <p>{t('Error loading data')}: {error}</p>
            <button onClick={selectedCategory ? () => fetchFAQs(selectedCategory.id) : fetchCategories}>
              {t('Retry')}
            </button>
          </div>
        )}

        {/* Category List */}
        {!selectedCategory && !loading && !error && (
          <div className="leave-balance-container">
            <ul className="leave-balance-list">
              {categories.map((cat) => (
                <li
                  key={cat.id}
                  className="leave-balance-item"
                  onClick={() => handleCategoryClick(cat)}
                >
                  <div className="main-icon">
                    <img src={getCategoryIcon(cat.category)} alt="" />
                  </div>
                  <div className="title">{cat.category}</div>
                  <div className="img-icon">
                    <img src={arrowLeft} alt="" />
                  </div>
                </li>
              ))}
            </ul>

            {categories.length === 0 && (
              <div className="no-categories-message">
                <p>{t('No categories available')}</p>
              </div>
            )}
          </div>
        )}

        {/* FAQ List */}
        {selectedCategory && !loading && !error && (
          <div className="faq-accordion-section">
            <Accordion faqs={faqs} />
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQs;